import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Modal } from "@/components/application/modals/modal-controlled";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import {
  MAX_FILE_BYTES,
  TAX_DOC_ERROR_CODES,
  VAT_RATES,
  type TaxDocumentUploadFormValues,
  type VatRate,
} from "../../schemas/taxDocumentUpload";

/**
 * UC-008 Krok 7 — Petr admin upload tax document (DEC-012 + Phase 22).
 *
 * Pattern (UUI Modal s drag&drop + RHF; manual Zod-style guard):
 *   1. Petr klikne na "Nahrát doklad" → modal open=true s budget detail.
 *   2. Drag file (PDF only, max 10 MB) nebo file picker.
 *   3. Vyplň document_number, issue_date, amount_kc, vat_rate, note.
 *   4. Submit → mutation POST /api/admin/months/{budgetId}/tax-document (multipart).
 *   5. Backend 10-step validation pipeline → 422 / 409 / 413 / 503 errors mapovány
 *      na uniform error_code message.
 *   6. Success: parent component fechtne refresh + zobrazí toast s attachments_count.
 *
 * Accessibility: Modal focus trap + Esc dismiss + aria-labelledby.
 *
 * R-033 guard: pokud `budgetApproved=false` → modal blokuje submit s Alert.
 */

interface FormFields {
  document_number: string;
  issue_date: string;
  amount_kc: number;
  vat_rate: VatRate;
  note: string;
  reason: string;
}

export interface TaxDocumentUploadModalProps {
  open: boolean;
  budgetId: string;
  monthLabel: string;
  /** Cíl tenant pro upload — typicky A&L soft (UC-008). */
  tenantId: string;
  /** True pouze pokud rozpočet schválen (UC-008 EC1 / R-033). */
  budgetApproved: boolean;
  /**
   * Pokud již existuje tax_document pro tento Budget, otevřeme modal v re-upload
   * (EC6) modu — increment version + email Davidovi s diff summary.
   */
  existingDocumentNumber?: string;
  existingAmountKc?: number;
  existingVersion?: number;
  submitting?: boolean;
  serverError?: { error_code: string; message: string } | null;
  onClose: () => void;
  onSubmit: (values: TaxDocumentUploadFormValues) => void;
}

const VAT_RATE_LABELS: Record<VatRate, string> = {
  standard_21: "21 % standard",
  zero: "0 % osvobozeno",
  not_subject: "Nepodléhá DPH",
};

export function TaxDocumentUploadModal({
  open,
  budgetId,
  monthLabel,
  tenantId: _tenantId,
  budgetApproved,
  existingDocumentNumber,
  existingAmountKc,
  existingVersion,
  submitting,
  serverError,
  onClose,
  onSubmit,
}: TaxDocumentUploadModalProps) {
  const isReUpload = !!existingDocumentNumber;
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const form = useForm<FormFields>({
    defaultValues: {
      document_number: existingDocumentNumber ?? "",
      issue_date: new Date().toISOString().slice(0, 10),
      amount_kc: existingAmountKc ?? 0,
      vat_rate: "standard_21",
      note: "",
      reason: "",
    },
  });
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      form.reset({
        document_number: existingDocumentNumber ?? "",
        issue_date: new Date().toISOString().slice(0, 10),
        amount_kc: existingAmountKc ?? 0,
        vat_rate: "standard_21",
        note: "",
        reason: "",
      });
      setFile(null);
      setFileError(null);
    }
  }, [open, existingDocumentNumber, existingAmountKc, form]);

  function validateAndSetFile(picked: File | null) {
    if (!picked) {
      setFile(null);
      setFileError(null);
      return;
    }
    if (picked.type !== "application/pdf") {
      setFile(null);
      setFileError("Pouze application/pdf typ");
      return;
    }
    if (picked.size === 0) {
      setFile(null);
      setFileError("Soubor je prázdný");
      return;
    }
    if (picked.size > MAX_FILE_BYTES) {
      setFile(null);
      setFileError(`Soubor je větší než ${Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB`);
      return;
    }
    setFile(picked);
    setFileError(null);
  }

  function handleFileChange(files: FileList | null) {
    const picked = files?.[0] ?? null;
    validateAndSetFile(picked);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    handleFileChange(event.dataTransfer.files);
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave() {
    setDragOver(false);
  }

  function onValidSubmit(values: FormFields) {
    if (!file) {
      setFileError("PDF soubor je povinný");
      return;
    }
    onSubmit({
      file,
      document_number: values.document_number.trim(),
      issue_date: values.issue_date,
      amount_kc: Number(values.amount_kc),
      vat_rate: values.vat_rate,
      note: values.note,
      reason: values.reason,
    });
  }

  const serverErrorMessage = serverError ? mapErrorMessage(serverError) : null;

  return (
    <Modal
      open={open}
      title={isReUpload ? `Re-upload daňového dokladu (v${(existingVersion ?? 1) + 1}) — ${monthLabel}` : `Nahrát daňový doklad — ${monthLabel}`}
      onClose={onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={() => void form.handleSubmit(onValidSubmit)()}
            isDisabled={!budgetApproved || submitting || !file}
            data-testid="taxdoc-upload-submit"
          >
            {submitting ? "Nahrávám…" : isReUpload ? "Nahrát novou verzi" : "Nahrát doklad"}
          </Button>
        </>
      }
    >
      <form
        className="taxdoc-upload-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onValidSubmit)(e);
        }}
      >
        {!budgetApproved && (
          <InlineAlert variant="warning" title="Rozpočet musí být schválen">
            Daňový doklad nelze nahrát před schválením rozpočtu. Schvalte rozpočet a vraťte se sem.
          </InlineAlert>
        )}

        {isReUpload && (
          <InlineAlert variant="info" title="Nahrání nové verze">
            Stávající verze v{existingVersion}: <strong>{existingDocumentNumber}</strong> ({existingAmountKc?.toLocaleString("cs-CZ")} Kč).
            Po uložení Davidovi přijde email s přehledem změn.
          </InlineAlert>
        )}

        {/* Drag & drop zone */}
        <div
          className={`taxdoc-upload-dropzone${dragOver ? " taxdoc-upload-dropzone--active" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Přetáhněte PDF sem nebo klikněte pro výběr"
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          data-testid="taxdoc-upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: "none" }}
            onChange={(e) => handleFileChange(e.target.files)}
            data-testid="taxdoc-upload-file"
          />
          {file ? (
            <>
              <p>
                <strong>{file.name}</strong> ({Math.round(file.size / 1024)} KB)
              </p>
              <Badge color="blue">PDF zvolen</Badge>
            </>
          ) : (
            <p>Přetáhněte PDF sem nebo klikněte pro výběr (max {Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB)</p>
          )}
        </div>
        {fileError && (
          <p role="alert" className="taxdoc-upload-form__error">
            {fileError}
          </p>
        )}

        <Controller
          control={form.control}
          name="document_number"
          rules={{
            required: "Číslo dokladu je povinné",
            maxLength: { value: 64, message: "Maximálně 64 znaků" },
          }}
          render={({ field, fieldState }) => (
            <Input
              label="Číslo dokladu"
              isRequired
              type="text"
              placeholder="2026-04-A&Lsoft"
              data-testid="taxdoc-document-number"
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              isInvalid={!!fieldState.error}
              hint={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name="issue_date"
          rules={{
            required: "Datum vystavení je povinné",
            validate: (value) =>
              Date.parse(value) <= Date.now() || "Datum vystavení nesmí být v budoucnosti",
          }}
          render={({ field, fieldState }) => (
            <Input
              label="Datum vystavení"
              isRequired
              type="date"
              data-testid="taxdoc-issue-date"
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              isInvalid={!!fieldState.error}
              hint={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name="amount_kc"
          rules={{
            required: "Částka je povinná",
            min: { value: 1, message: "Větší než 0" },
          }}
          render={({ field, fieldState }) => (
            <Input
              label="Částka (Kč, bez haléřů)"
              isRequired
              type="number"
              inputMode="numeric"
              data-testid="taxdoc-amount"
              name={field.name}
              value={field.value === undefined || field.value === null ? "" : String(field.value)}
              onChange={(v) => field.onChange(v === "" ? "" : Number(v))}
              onBlur={field.onBlur}
              isInvalid={!!fieldState.error}
              hint={fieldState.error?.message}
            />
          )}
        />

        <fieldset
          aria-describedby={
            form.formState.errors.vat_rate ? "taxdoc-vat-error" : undefined
          }
          className="taxdoc-upload-form__vat-fieldset"
        >
          <legend className="text-sm font-medium text-secondary">Sazba DPH</legend>
          {VAT_RATES.map((rate) => (
            <label key={rate} className="taxdoc-upload-form__radio">
              <input
                type="radio"
                value={rate}
                data-testid={`taxdoc-vat-${rate}`}
                {...form.register("vat_rate", { required: "Vyber sazbu DPH" })}
              />
              <span>{VAT_RATE_LABELS[rate]}</span>
            </label>
          ))}
          {form.formState.errors.vat_rate && (
            <p id="taxdoc-vat-error" className="text-sm text-error-primary">
              {form.formState.errors.vat_rate.message}
            </p>
          )}
        </fieldset>

        <Controller
          control={form.control}
          name="note"
          rules={{ maxLength: { value: 500, message: "Maximálně 500 znaků" } }}
          render={({ field, fieldState }) => (
            <TextArea
              label="Poznámka (interní, nezasílá se Davidovi)"
              rows={3}
              data-testid="taxdoc-note"
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              isInvalid={!!fieldState.error}
              hint={fieldState.error?.message}
            />
          )}
        />

        {isReUpload && (
          <Controller
            control={form.control}
            name="reason"
            rules={{ maxLength: { value: 500, message: "Maximálně 500 znaků" } }}
            render={({ field, fieldState }) => (
              <TextArea
                label="Důvod nové verze (audit)"
                rows={2}
                placeholder="Např. oprava chyby v původním dokladu z fakturačního SW"
                data-testid="taxdoc-reason"
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                isInvalid={!!fieldState.error}
                hint={fieldState.error?.message}
              />
            )}
          />
        )}

        {serverErrorMessage && (
          <InlineAlert variant="error" title="Nahrání selhalo" data-testid="taxdoc-upload-error">
            {serverErrorMessage}
          </InlineAlert>
        )}

        {/* Hidden field — for backend tenant_id binding. */}
        <input type="hidden" name="budget_id" value={budgetId} />
      </form>
    </Modal>
  );
}

function mapErrorMessage(err: { error_code: string; message: string }): string {
  switch (err.error_code) {
    case TAX_DOC_ERROR_CODES.MALWARE_DETECTED:
      return "Soubor obsahuje škodlivý kód a byl odmítnut.";
    case TAX_DOC_ERROR_CODES.MALWARE_SCAN_UNAVAILABLE:
      return "Antivirový sken není dočasně dostupný. Zkuste znovu za pár minut.";
    case TAX_DOC_ERROR_CODES.FILE_TOO_LARGE:
      return "Soubor je větší než 10 MB.";
    case TAX_DOC_ERROR_CODES.INVALID_MAGIC_BYTES:
      return "Soubor není platné PDF.";
    case TAX_DOC_ERROR_CODES.PDF_PARSE_FAILED:
      return "PDF se nepodařilo načíst (poškozený soubor).";
    case TAX_DOC_ERROR_CODES.BUDGET_NOT_APPROVED:
      return "Rozpočet musí být schválen.";
    case TAX_DOC_ERROR_CODES.DUPLICATE_CONTENT:
      return "Tento doklad už byl nahrán dříve.";
    case TAX_DOC_ERROR_CODES.DUPLICATE_DOCUMENT_NUMBER:
      return "Číslo dokladu už bylo použito pro tento rozpočet.";
    default:
      return err.message || `Chyba: ${err.error_code}`;
  }
}
